import './score-counter.css';

export interface ScoreCounterOptions {
  initial?: number;
  format?: (n: number) => string;
}

export interface ScoreCounterHandle {
  readonly el: HTMLElement;
  set(value: number): void;
  bump(delta: number): void;
  current(): number;
  destroy(): void;
}

const DEFAULT_FORMAT = (n: number) => n.toLocaleString();
const PULSE_MS = 240;
const FLOATER_MS = 800;
const COUNT_MS = 500;

export function createScoreCounter(opts: ScoreCounterOptions = {}): ScoreCounterHandle {
  let value = opts.initial ?? 0;
  const format = opts.format ?? DEFAULT_FORMAT;

  const el = document.createElement('div');
  el.className = 'fg-score';

  const numEl = document.createElement('div');
  numEl.className = 'fg-score__num';
  numEl.textContent = format(value);
  el.appendChild(numEl);

  let countRafId: number | null = null;

  function setNumberDisplay(n: number) {
    numEl.textContent = format(n);
  }

  function animateTo(start: number, target: number) {
    const dur = COUNT_MS;
    const t0 = performance.now();
    if (countRafId !== null) cancelAnimationFrame(countRafId);
    function tick(now: number) {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - (1 - p) ** 3;
      const v = Math.round(start + (target - start) * eased);
      setNumberDisplay(v);
      if (p < 1) countRafId = requestAnimationFrame(tick);
      else {
        setNumberDisplay(target);
        countRafId = null;
      }
    }
    countRafId = requestAnimationFrame(tick);
  }

  function pulse() {
    numEl.classList.add('fg-score__num--pulse');
    setTimeout(() => numEl.classList.remove('fg-score__num--pulse'), PULSE_MS);
  }

  function spawnFloater(delta: number) {
    const f = document.createElement('div');
    f.className = 'fg-score__floater';
    f.textContent = (delta >= 0 ? '+' : '') + delta;
    el.appendChild(f);
    requestAnimationFrame(() => f.classList.add('fg-score__floater--fly'));
    setTimeout(() => f.remove(), FLOATER_MS);
  }

  function set(next: number) {
    const prev = value;
    if (next === prev) {
      setNumberDisplay(next);
      return;
    }
    value = next;
    pulse();
    animateTo(prev, next);
  }

  function bump(delta: number) {
    spawnFloater(delta);
    set(value + delta);
  }

  return {
    el,
    set,
    bump,
    current: () => value,
    destroy() {
      if (countRafId !== null) cancelAnimationFrame(countRafId);
      el.remove();
    },
  };
}
